import { ChangeDetectionStrategy, Component, computed, input, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { PrefixUrlPipe } from '../../pipes/prefix-url.pipe';

export type EventqCameraGridLayout = '1x1' | '2x2' | '3x3' | '4x3';

export interface EventqCameraGridSize {
  readonly cols: number;
  readonly rows: number;
}

/** Accepts legacy `4x4` (and unknown values) so hot state / older tabs do not crash. */
export function normalizeCameraGridLayout(
  layout: string | null | undefined,
): EventqCameraGridLayout {
  switch (layout) {
    case '1x1':
    case '2x2':
    case '3x3':
    case '4x3':
      return layout;
    case '4x4':
      return '4x3';
    default:
      return '2x2';
  }
}

export function cameraGridSize(layout: string | null | undefined): EventqCameraGridSize {
  switch (normalizeCameraGridLayout(layout)) {
    case '1x1':
      return { cols: 1, rows: 1 };
    case '2x2':
      return { cols: 2, rows: 2 };
    case '3x3':
      return { cols: 3, rows: 3 };
    case '4x3':
      return { cols: 4, rows: 3 };
  }
}

export function cameraGridDimension(layout: string | null | undefined): number {
  return cameraGridSize(layout).cols;
}

export function cameraGridCellsPerPage(layout: string | null | undefined): number {
  const size = cameraGridSize(layout);
  return size.cols * size.rows;
}

@Component({
  selector: 'eventq-camera-grid',
  imports: [NgTemplateOutlet, PrefixUrlPipe],
  templateUrl: './camera-grid.html',
  styleUrl: './camera-grid.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--eventq-camera-grid-cols]': 'cols()',
    '[style.--eventq-camera-grid-rows]': 'rows()',
  },
})
export class EventqCameraGrid {
  readonly layout = input<EventqCameraGridLayout | string>('2x2');
  /** Ordered camera ids for the current page (may be shorter than cellsPerPage). */
  readonly cameraIds = input.required<readonly string[]>();
  /** Optional per-slot content; context: `{ $implicit: cameraId, index: number }`. */
  readonly cellTemplate = input<TemplateRef<{ $implicit: string; index: number }> | null>(null);
  readonly emptyIconSrc = input('/assets/events/icons/security-camera.svg');
  readonly emptyLabel = input('Select Camera');

  readonly size = computed(() => cameraGridSize(this.layout()));
  readonly cols = computed(() => this.size().cols);
  readonly rows = computed(() => this.size().rows);
  readonly cellsPerPage = computed(() => cameraGridCellsPerPage(this.layout()));
  readonly slots = computed(() => {
    const ids = this.cameraIds();
    const count = this.cellsPerPage();
    return Array.from({ length: count }, (_, index) => ids[index] ?? null);
  });
}
